import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Pagination,
  MenuItem,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import usePageTitle from "app/pageTitle";
import MDButton from "theme/components/MDButton";
import MDInput from "theme/components/MDInput";
import DataTable from "theme/Tables/DataTable";
import axios from "axios.js";
import { ref } from "yup";

function WheelSpinsPage() {
  usePageTitle("Spin & Win - Wheel Spins");

  const navigate = useNavigate();
  const location = useLocation();
  const { ref } = useParams();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");

  const [spins, setSpins] = useState([]);
  const [segments, setSegments] = useState([]);
  const [pageLoaded, setPageLoaded] = useState(false);

  const [search, setSearch] = useState("");
  const [segmentReference, setSegmentReference] = useState("");
  const [won, setWon] = useState("");
  const [disbursed, setDisbursed] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    per_page: 20,
    total_pages: 1,
    has_next: false,
    has_previous: false,
  });

  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const showMessage = (message, messageSeverity = "error") => {
    setSeverity(messageSeverity);
    setErrMsg(message || "An unexpected error occurred.");
    setOpen(true);
  };

  const handleRequestError = () => {
    setShowLoading(false);
    showMessage(
      `Unable to complete request. ${
        navigator.onLine ? "" : "Make sure you have an active internet connection."
      }`
    );
  };

  const fetchSpins = async (requestedPage = page, requestedPerPage = perPage) => {
    if (!ref) {
      showMessage("Wheel reference is missing.");
      return;
    }

    setShowLoading(true);

    const queryParams = {
      page: requestedPage,
      per_page: requestedPerPage,
    };

    if (search.trim()) {
      queryParams.search = search.trim();
    }

    if (segmentReference) {
      queryParams.segment_reference = segmentReference;
    }

    if (won !== "") {
      queryParams.won = won;
    }

    if (disbursed !== "") {
      queryParams.disbursed = disbursed;
    }

    if (startDate) {
      queryParams.start_date = startDate;
    }

    if (endDate) {
      queryParams.end_date = endDate;
    }

    try {
      const result = await axios.get(`/portal/tm/spin/promotions/wheels/${ref}/spins.json`, {
        params: queryParams,
      });

      if (result.data.status === "success") {
        const data = result.data.data;

        const rows = (data.spins || []).map((row) => ({
          participant: (
            <MDBox display="flex" flexDirection="column">
              <MDTypography variant="button" fontWeight="medium">
                {row.participant_name || "---"}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                {row.email_address || "---"}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                {row.phone_number || "---"}
              </MDTypography>
            </MDBox>
          ),
          segment: row.segment_title || "---",
          won: (
            <MDTypography variant="button" fontWeight="medium" color={row.won ? "success" : "text"}>
              {row.won ? "Won" : "Not Won"}
            </MDTypography>
          ),
          disbursed: (
            <MDTypography
              variant="button"
              fontWeight="medium"
              color={row.disbursed ? "success" : "text"}
            >
              {row.disbursed ? "Yes" : "No"}
            </MDTypography>
          ),
          spun_on: row.spun_on || "---",
          created: row.created || "---",
        }));

        setSpins(rows);
        setSegments(data.segments || []);

        if (data.pagination) {
          setPagination(data.pagination);
          setPage(data.pagination.page);
          setPerPage(data.pagination.per_page);
        }

        setPageLoaded(true);
      } else {
        showMessage(result.data.data.message);
      }
    } catch (error) {
      handleRequestError();
      return;
    }

    setShowLoading(false);
  };

  useEffect(() => {
    if (ref) {
      fetchSpins(1, perPage);
    }
  }, [ref]);

  const applyFilters = () => {
    setPage(1);
    fetchSpins(1, perPage);
  };

  const clearFilters = () => {
    setSearch("");
    setSegmentReference("");
    setWon("");
    setDisbursed("");
    setStartDate("");
    setEndDate("");
    setPage(1);

    setShowLoading(true);

    axios
      .get(`/portal/tm/spin/promotions/wheels/${ref}/spins.json`, {
        params: {
          page: 1,
          per_page: perPage,
        },
      })
      .then((result) => {
        if (result.data.status === "success") {
          const data = result.data.data;

          setSpins(
            (data.spins || []).map((row) => ({
              participant: (
                <MDBox display="flex" flexDirection="column">
                  <MDTypography variant="button" fontWeight="medium">
                    {row.participant_name || "---"}
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    {row.email_address || "---"}
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    {row.phone_number || "---"}
                  </MDTypography>
                </MDBox>
              ),
              segment: row.segment_title || "---",
              won: (
                <MDTypography
                  variant="button"
                  fontWeight="medium"
                  color={row.won ? "success" : "text"}
                >
                  {row.won ? "Won" : "Not Won"}
                </MDTypography>
              ),
              disbursed: (
                <MDTypography
                  variant="button"
                  fontWeight="medium"
                  color={row.disbursed ? "success" : "text"}
                >
                  {row.disbursed ? "Yes" : "No"}
                </MDTypography>
              ),
              spun_on: row.spun_on || "---",
              created: row.created || "---",
            }))
          );

          setSegments(data.segments || []);

          if (data.pagination) {
            setPagination(data.pagination);
            setPage(data.pagination.page);
            setPerPage(data.pagination.per_page);
          }

          setPageLoaded(true);
        } else {
          showMessage(result.data.data.message);
        }
      })
      .catch(() => {
        handleRequestError();
      })
      .finally(() => {
        setShowLoading(false);
      });
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    fetchSpins(value, perPage);
  };

  const handlePerPageChange = (event) => {
    const value = Number(event.target.value);
    setPerPage(value);
    setPage(1);
    fetchSpins(1, value);
  };

  const tableColumns = [
    {
      Header: "Participant",
      accessor: "participant",
      align: "left",
    },
    {
      Header: "Segment",
      accessor: "segment",
      align: "left",
    },
    {
      Header: "Result",
      accessor: "won",
      align: "center",
    },
    {
      Header: "Disbursed",
      accessor: "disbursed",
      align: "center",
    },
    {
      Header: "Date",
      accessor: "spun_on",
      align: "right",
    },
    // {
    //   Header: "Recorded",
    //   accessor: "created",
    //   align: "left",
    // },
  ];

  const exportSpins = async (format) => {
    if (!ref) {
      showMessage("Wheel reference is missing.");
      return;
    }

    const params = {};

    if (search.trim()) params.search = search.trim();
    if (segmentReference) params.segment_reference = segmentReference;
    if (won !== "") params.won = won;
    if (disbursed !== "") params.disbursed = disbursed;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    params.format = format;

    setShowLoading(true);

    try {
      const response = await axios.get(
        `/portal/tm/spin/promotions/wheels/${ref}/spins/export.json`,
        {
          params,
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], {
        type:
          format === "xlsx"
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "text/csv;charset=utf-8;",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `wheel_spins.${format === "xlsx" ? "xlsx" : "csv"}`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      showMessage("Unable to export spins.");
    } finally {
      setShowLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <MDBox mt={1} mb={5}>
        <Card>
          <MDBox p={3}>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              flexWrap="wrap"
              gap={2}
            >
              <MDBox>
                <MDTypography variant="h5" fontWeight="bold">
                  Wheel Spins
                </MDTypography>
                <MDTypography mt={1} variant="button" fontWeight="light" color="text">
                  Review spin results and prize disbursement status.
                </MDTypography>
              </MDBox>

              <MDBox display="flex" gap={1} flexWrap="wrap">
                <MDButton
                  variant="outlined"
                  color="dark"
                  onClick={() => navigate(-1)}
                  startIcon={<Icon>arrow_back</Icon>}
                  size="small"
                >
                  Back
                </MDButton>

                {/* <MDButton
                  variant="outlined"
                  color="info"
                  onClick={() => navigate(`/tm/spin/promotions/wheels/participants/${ref}`)}
                  startIcon={<Icon>people</Icon>}
                  size="small"
                >
                  Participants
                </MDButton> */}
              </MDBox>
            </MDBox>
          </MDBox>

          <MDBox px={3} pb={2}>
            <MDTypography variant="h6" fontWeight="medium">
              Filter Spins
            </MDTypography>

            <MDBox
              mt={2}
              display="grid"
              gridTemplateColumns={{
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(3, minmax(0, 1fr))",
              }}
              gap={2}
            >
              <MDInput
                label="Search participant or segment"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                fullWidth
              />

              <TextField
                select
                label="Segment"
                value={segmentReference}
                onChange={(event) => setSegmentReference(event.target.value)}
                fullWidth
                size="medium"
                sx={{
                  minWidth: 0,
                  "& .MuiInputBase-root": {
                    minHeight: 45,
                  },
                  "& .MuiSelect-select": {
                    minHeight: "45px !important",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    paddingTop: "10px",
                    paddingBottom: "10px",
                  },
                }}
              >
                <MenuItem value="">All Segments</MenuItem>
                {segments.map((segment) => (
                  <MenuItem key={segment.reference} value={segment.reference}>
                    {segment.title}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Result"
                value={won}
                onChange={(event) => setWon(event.target.value)}
                fullWidth
                sx={{
                  minWidth: 0,
                  "& .MuiInputBase-root": {
                    minHeight: 45,
                  },
                  "& .MuiSelect-select": {
                    minHeight: "45px !important",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    paddingTop: "10px",
                    paddingBottom: "10px",
                  },
                }}
              >
                <MenuItem value="">All Results</MenuItem>
                <MenuItem value="true">Won</MenuItem>
                <MenuItem value="false">Not Won</MenuItem>
              </TextField>

              <TextField
                select
                label="Disbursement"
                value={disbursed}
                onChange={(event) => setDisbursed(event.target.value)}
                fullWidth
                sx={{
                  minWidth: 0,
                  "& .MuiInputBase-root": {
                    minHeight: 45,
                  },
                  "& .MuiSelect-select": {
                    minHeight: "45px !important",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    paddingTop: "10px",
                    paddingBottom: "10px",
                  },
                }}
              >
                <MenuItem value="">All Disbursement Statuses</MenuItem>
                <MenuItem value="true">Disbursed</MenuItem>
                <MenuItem value="false">Not Disbursed</MenuItem>
              </TextField>

              <MDInput
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />

              <MDInput
                label="End Date"
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </MDBox>

            <MDBox
              mt={2}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
            >
              <MDBox display="flex" gap={1}>
                <MDButton
                  variant="contained"
                  color="dark"
                  onClick={applyFilters}
                  startIcon={<Icon>search</Icon>}
                  size="small"
                >
                  Apply Filters
                </MDButton>

                <MDButton
                  variant="outlined"
                  color="dark"
                  onClick={() => exportSpins("xlsx")}
                  startIcon={<Icon>download</Icon>}
                  size="small"
                >
                  Export
                </MDButton>

                <MDButton
                  variant="outlined"
                  color="dark"
                  onClick={clearFilters}
                  startIcon={<Icon>restart_alt</Icon>}
                  size="small"
                >
                  Clear
                </MDButton>
              </MDBox>

              <TextField
                select
                label="Rows per page"
                value={perPage}
                onChange={handlePerPageChange}
                size="small"
                sx={{
                  minWidth: 140,
                  "& .MuiInputBase-root": {
                    minHeight: 45,
                  },
                  "& .MuiSelect-select": {
                    minHeight: "45px !important",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    paddingTop: "10px",
                    paddingBottom: "10px",
                  },
                }}
              >
                {[20, 50, 100].map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </TextField>
            </MDBox>
          </MDBox>

          {pageLoaded && (
            <MDBox mt={5}>
              {spins.length > 0 ? (
                <>
                  <MDBox p={0}>
                    <DataTable
                      table={{
                        columns: tableColumns,
                        rows: spins,
                      }}
                      canSearch={false}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      buttons
                      noEndBorder
                    />
                  </MDBox>

                  <MDBox
                    px={3}
                    py={2}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={2}
                  >
                    <MDTypography variant="button" color="text">
                      {pagination.total} spin
                      {pagination.total === 1 ? "" : "s"} found
                      {" · "}Page {pagination.page} of {pagination.total_pages}
                    </MDTypography>

                    <Pagination
                      count={Math.max(1, pagination.total_pages)}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                      size="small"
                      disabled={showLoading || pagination.total_pages <= 1}
                    />
                  </MDBox>
                </>
              ) : (
                <MDBox p={3}>
                  <MDTypography variant="button" color="text">
                    No spins match the selected filters.
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>
          )}
        </Card>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default WheelSpinsPage;
